import type { VoiceOption } from "@/components/agents/setup/types"
import {
  DEFAULT_HOURS,
  type DayHours,
  type HoursStatus,
} from "@/components/business-profile/hours-editor"

// IMP-388: hours types/defaults now live with the shared HoursEditor. Re-export
// so existing onboarding + admin importers keep their import path.
export { DEFAULT_HOURS, type DayHours, type HoursStatus }

// IMP-457: the assistant-led flow. "intro" and "analyzing" are client-side
// beats inside the server's "voice" and "search" phases; the server persists
// voice | search | details | agents on the account.
export type Step =
  | "intro"
  | "voice"
  | "business"
  | "analyzing"
  | "profile"
  | "jobs"

// IMP-390: an as-you-type autocomplete prediction. Carries only the business
// name and its locality ("Bend, OR") — the website and address are resolved
// via the place-details endpoint when the user selects it, so no raw URL ever
// appears in the dropdown.
export interface BusinessSuggestion {
  place_id: string
  name: string
  locality: string | null
}

export interface BusinessProfilePayload {
  business_name: string | null
  phone: string | null
  address: string | null
  website: string | null
  business_category: string | null
  description: string | null
  services: string | null
  hours: DayHours[] | null
  hours_status: HoursStatus | null
  place_id: string | null
  // IMP-458: detected from the website by the scrape; null when undetected
  // (the UI shows an editable default).
  brand_color: string | null
}

// IMP-457: one entry per job on the closing screen — purchased jobs carry the
// agent to deep-link into; unpurchased ones carry their add-on price.
export interface OnboardingJob {
  uid: string
  slug: string
  name: string
  description: string | null
  purchased: boolean
  agent_uid: string | null
  retail_price_cents: number | null
}

export interface OnboardingPageProps {
  account: { uid: string; onboarding_step: string; state: string }
  business_profile: BusinessProfilePayload | null
  jobs: OnboardingJob[]
  voices: VoiceOption[]
  // IMP-240: human labels for the free-text "Industry" autocomplete.
  industry_options: string[]
  // IMP-210: pre-provisioned accounts skip search + scrape.
  truncated: boolean
  // IMP-287: post-purchase self-serve onboarding (website entry → confirm).
  post_purchase: boolean
}

// Map the server-persisted step onto the client flow.
export function stepForAccount(step: string): Step {
  if (step === "details") return "profile"
  if (step === "agents") return "jobs"
  if (step === "search") return "business"
  return "intro"
}

// Flow order, used to only ever sync the local step forward.
export const STEP_ORDER: Step[] = [
  "intro",
  "voice",
  "business",
  "analyzing",
  "profile",
  "jobs",
]

export function emptyProfile(): BusinessProfilePayload {
  return {
    business_name: "",
    phone: "",
    address: "",
    website: "",
    business_category: "",
    description: "",
    services: "",
    hours: DEFAULT_HOURS,
    // IMP-388: "open, hours not set" is the safe default until real hours are
    // found — never a fabricated 9-5 week.
    hours_status: "open_no_hours",
    place_id: null,
    brand_color: null,
  }
}

const URL_RE = /^(https?:\/\/)?(www\.)?[a-z0-9-]+\.[a-z]{2,}/i

export function detectDomain(q: string): string | null {
  const trimmed = q.trim()
  if (!URL_RE.test(trimmed)) return null
  return trimmed
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0]
}
